import * as posActionTypes from "../constants/posActionTypes";
import * as posModels from "./api/posModels";
import * as reduxModels from "./reduxModels";
import {Reducer} from "redux";
import {API} from "habit-core";

function createReducer<T>(
    initialState: T,
    handlers: {[actionType: string]: Reducer<T>},
): Reducer<T> {
    return function reducer(state = initialState, action) {
        if ({}.hasOwnProperty.call(handlers, action.type)) {
            return handlers[action.type](state, action);
        } else {
            return state;
        }
    };
}

export const NO_TILL = -1;

export type PosState = {
    deviceId: string | null;
    app: {
        isParallel: boolean;
    };
    store: {
        id: string | null;
        penniesUnavailable: boolean;
        kioskCashOrderPagerNumber: number | null;
        charitySettings: posModels.CharitySettings;
        supportedExternalTenders: posModels.ExternalTender[];
        isLab: boolean;
        taxExemptEnabled: boolean;
        autoComboEnabled: boolean;
    };
    station: {
        mode: posModels.StationMode;
        requiresDriveThruDetails: boolean;
        laneAssignment: posModels.LaneAssignment | null;
        cashier: reduxModels.Cashier;
        autoPrintReceipts: boolean;
        isTraining: boolean;
        isCCPaymentDisabled: boolean;
        isSubmittingOrder: boolean;

        assignedTill: number; // -1 means NO TILL
        tillAssignmentSkipped: boolean;
        trainingModeAssignedTill: number;
        isAssigningTill: boolean;
    };
    currentOrder: {
        id: string | null;
        orderNumber: number | null;
        referenceNumber: number | null;
        voidedItemIds: string[];
        voidedComboIds: string[];
        guestName: string | null;
        pagerNumber: number | null;
        driveThruDetails: posModels.DriveThruDetails;
        discounts: posModels.DiscountDetails[];
        giftCards: {
            addFunds: posModels.GiftCardAddFundsCartEntity[];
            purchase: posModels.GiftCardPurchaseCartEntity[];
        };
        startTime: Date | null;
        endTime: Date | null;
        payment: posModels.CurrentOrderPayment;
        qsrSelections: posModels.QsrSelection[] | null;
        charityAmountCents: API.models.USDCents | null;
        isTaxExempt: boolean;
        allergyWarningShown: boolean;
        resetOnCartEmptied: boolean;
        ezCaterOrderType: "pickup" | "delivery" | undefined;
        havePaymentsCoveredBalance: boolean; // upon payment, will be set to true if balance due is 0
        isSubmitted: boolean;
        printReceipt: boolean;
    };
    // state used for holding all "closed" orders - "paid", "cancelled", "error"
    orders: {
        byId: {[orderId: string]: posModels.OrderParsed};
    };
    // state used to hold only pending orders
    pendingOrders: {
        byId: {[orderId: string]: posModels.OrderParsed};
    };
    currentOrderVoided: {
        items: {
            pending: {
                byId: {
                    [id: string]: posModels.PendingVoidedItem;
                };
            };
            voided: {
                byId: {
                    [id: string]: posModels.VoidedItem;
                };
            };
        };
        combos: {
            pending: {
                byId: {
                    [id: string]: posModels.PendingVoidedCombo;
                };
            };
            voided: {
                byId: {
                    [id: string]: posModels.VoidedCombo;
                };
            };
        };
    };
    allPreps: {
        byId: {
            [id: string]: posModels.AllPrepsItemModifierData;
        };
    };
    addOns: {
        byId: {
            [id: string]: string[];
        };
    };
    errors: {
        reserveOrderIdErrored: boolean;
        placeOrderAfterPaymentsProcessedErrored: boolean;
        tillAssignmentErrorMessage: string | undefined;
        currentOrderPlacmentErrorMessage: string | undefined;
        lastCartEntityRemovalErrorMessage: string | undefined;
        discountErrorMessage: string | undefined;
    };
};

const defaultState: PosState = {
    deviceId: null,
    app: {
        isParallel: false,
    },
    store: {
        id: null,
        penniesUnavailable: false,
        kioskCashOrderPagerNumber: null,
        charitySettings: {
            isEnabled: false,
            mode: null,
            charityName: null,
            prompt: null,
            rewardMinimumCents: null,
            rewardMessage: null,
        },
        supportedExternalTenders: [],
        isLab: false,
        taxExemptEnabled: false,
        autoComboEnabled: true,
    },
    station: {
        mode: "front_of_house_1",
        requiresDriveThruDetails: false,
        laneAssignment: null,
        cashier: {type: "user"},
        autoPrintReceipts: false,
        isTraining: false,
        isCCPaymentDisabled: false,
        isSubmittingOrder: false,

        assignedTill: NO_TILL,
        trainingModeAssignedTill: NO_TILL,
        isAssigningTill: false,
        tillAssignmentSkipped: false,
    },
    currentOrder: {
        id: null,
        orderNumber: null,
        referenceNumber: null,
        voidedItemIds: [],
        voidedComboIds: [],
        guestName: null,
        pagerNumber: null,
        driveThruDetails: {
            vehicleType: "",
            vehicleColor: "",
            eatingInCar: false,
            laneNumber: 1,
        },
        discounts: [],
        giftCards: {addFunds: [], purchase: []},
        startTime: null,
        endTime: null,
        payment: {
            cashPaidCents: 0,
            changeOwedCents: 0,
            giftCardsApplied: [],
            compCardsApplied: [],
            creditCardsApplied: [],
            ezCaterApplied: false,
            externalTendersApplied: [],
        },
        qsrSelections: null,
        charityAmountCents: null,
        isTaxExempt: false,
        allergyWarningShown: false,
        resetOnCartEmptied: false,
        ezCaterOrderType: undefined,
        havePaymentsCoveredBalance: false,
        isSubmitted: false,
        printReceipt: false,
    },
    orders: {
        byId: {},
    },
    pendingOrders: {
        byId: {},
    },
    currentOrderVoided: {
        items: {
            pending: {
                byId: {},
            },
            voided: {
                byId: {},
            },
        },
        combos: {
            pending: {
                byId: {},
            },
            voided: {
                byId: {},
            },
        },
    },
    allPreps: {
        byId: {},
    },
    addOns: {
        byId: {},
    },
    errors: {
        reserveOrderIdErrored: false,
        placeOrderAfterPaymentsProcessedErrored: false,
        tillAssignmentErrorMessage: undefined,
        currentOrderPlacmentErrorMessage: undefined,
        lastCartEntityRemovalErrorMessage: undefined,
        discountErrorMessage: undefined,
    },
};

const POS_SET_DEVICE_ID: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {deviceId} = action;
    return {
        ...state,
        deviceId,
    };
};

const POS_SET_APP_IS_PARALLEL: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {isParallel} = action;
    return {
        ...state,
        app: {
            ...state.app,
            isParallel,
        },
    };
};

const POS_STATION_INITIALIZE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {mode, requiresDriveThruDetails, laneAssignment, autoPrintReceipts} =
        action;
    const currentOrderLaneNumber = laneAssignment === "lane_2" ? 2 : 1;

    return {
        ...state,
        station: {
            ...state.station,
            mode,
            requiresDriveThruDetails,
            laneAssignment,
            autoPrintReceipts,
        },
        currentOrder: {
            ...state.currentOrder,
            driveThruDetails: {
                ...state.currentOrder.driveThruDetails,
                laneNumber: currentOrderLaneNumber,
            },
        },
    };
};

const POS_STATION_EDIT_MODE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {stationMode, laneAssignment} = action;
    const currentOrderLaneNumber = laneAssignment === "lane_2" ? 2 : 1;

    return {
        ...state,
        station: {
            ...state.station,
            mode: stationMode,
            laneAssignment: laneAssignment,
        },
        currentOrder: {
            ...state.currentOrder,
            driveThruDetails: {
                ...state.currentOrder.driveThruDetails,
                laneNumber: currentOrderLaneNumber,
            },
        },
    };
};

const POS_STATION_EDIT_AUTO_PRINT_RECEIPTS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {autoPrintReceipts} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            printReceipt: autoPrintReceipts,
        },
        station: {
            ...state.station,
            autoPrintReceipts,
        },
    };
};

const POS_STATION_SET_IS_CC_PAYMENT_DISABLED: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {isCCPaymentDisabled} = action;
    return {
        ...state,
        station: {
            ...state.station,
            isCCPaymentDisabled,
        },
    };
};

const POS_STATION_SET_IS_SUBMITTING_ORDER: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {isSubmittingOrder} = action;
    return {
        ...state,
        station: {
            ...state.station,
            isSubmittingOrder,
        },
    };
};

const POS_STATION_TOGGLE_TRAINING_MODE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {isTraining} = action;
    return {
        ...state,
        station: {
            ...state.station,
            isTraining,
        },
    };
};

const POS_STATION_SET_CASHIER: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {cashier} = action;
    return {
        ...state,
        station: {
            ...state.station,
            cashier,
        },
    };
};

const POS_STATION_SET_ASSIGNED_TILL: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {assignedTill} = action;
    return {
        ...state,
        station: {
            ...state.station,
            assignedTill,
        },
    };
};
const POS_STATION_SET_TRAINING_MODE_ASSIGNED_TILL: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {trainingModeAssignedTill} = action;
    return {
        ...state,
        station: {
            ...state.station,
            trainingModeAssignedTill,
        },
    };
};

const POS_STATION_SET_ASSIGNING_TILL: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {isAssigningTill} = action;
    return {
        ...state,
        station: {
            ...state.station,
            isAssigningTill,
        },
    };
};

const POS_STATION_SET_TILL_ASSIGNMENT_SKIPPED: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {tillAssignmentSkipped} = action;
    return {
        ...state,
        station: {
            ...state.station,
            tillAssignmentSkipped,
        },
    };
};

const POS_SET_STORE_SETTINGS: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {
        id,
        penniesUnavailable,
        kioskCashOrderPagerNumber,
        charitySettings,
        supportedExternalTenders,
        isLab,
        taxExemptEnabled,
        autoComboEnabled,
    } = action;
    return {
        ...state,
        store: {
            ...state.store,
            id,
            penniesUnavailable,
            kioskCashOrderPagerNumber,
            charitySettings,
            supportedExternalTenders,
            isLab,
            taxExemptEnabled,
            autoComboEnabled,
        },
    };
};

const POS_UPDATE_STORE_SETTINGS: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {penniesUnavailable, kioskCashOrderPagerNumber} = action;
    return {
        ...state,
        store: {
            ...state.store,
            penniesUnavailable,
            kioskCashOrderPagerNumber,
        },
    };
};

const POS_ALL_PREPS_INITIALIZE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const byId: {[x: string]: posModels.AllPrepsItemModifierData} = {};
    const {preps} = action;
    preps.forEach((prep: posModels.AllPrepsItemModifierData) => {
        byId[prep.id] = prep;
    });

    return {
        ...state,
        allPreps: {
            byId: {
                ...state.allPreps.byId,
                ...byId,
            },
        },
    };
};

const POS_ADD_ONS_INITIALIZE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const byId: {[x: string]: string[]} = {};
    const {itemsAddOnsInfo} = action;
    itemsAddOnsInfo.forEach((itemAddOnInfo: posModels.ItemAddOnsInfo) => {
        byId[itemAddOnInfo.itemId] = itemAddOnInfo.addOnItemIds;
    });

    return {
        ...state,
        addOns: {
            byId: {
                ...state.addOns.byId,
                ...byId,
            },
        },
    };
};

const POS_CURRENT_ORDER_SET_ID: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            id,
        },
    };
};

const POS_CURRENT_ORDER_SET_REF_NUM: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {referenceNumber} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            referenceNumber,
        },
    };
};

const POS_CURRENT_ORDER_SET_ID_REF_NUM_AND_NUMBER: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, referenceNumber, orderNumber} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            id,
            referenceNumber,
            orderNumber,
        },
    };
};

const POS_CURRENT_ORDER_REMOVE_REF_NUM: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            referenceNumber: null,
        },
    };
};

const POS_CURRENT_ORDER_CLEAR: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    const nextOrderLaneNumber =
        state.station.laneAssignment === "lane_2" ? 2 : 1;

    return {
        ...state,
        currentOrder: {
            ...defaultState.currentOrder,
            printReceipt: state.station.autoPrintReceipts,
            driveThruDetails: {
                ...defaultState.currentOrder.driveThruDetails,
                laneNumber: nextOrderLaneNumber,
            },
        },
    };
};

const POS_CURRENT_ORDER_SET_DRIVE_THRU_DETAILS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {driveThruDetails} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            driveThruDetails,
        },
    };
};

const POS_CURRENT_ORDER_SET_CUSTOMER_INFO: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {guestName, pagerNumber} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            guestName,
            pagerNumber,
        },
    };
};

const POS_CURRENT_ORDER_SET_START_TIME: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {startTime} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            startTime,
        },
    };
};

const POS_CURRENT_ORDER_SET_END_TIME: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {endTime} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            endTime,
        },
    };
};

const POS_CURRENT_ORDER_SET_ORDER_NUMBER: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {orderNumber} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            orderNumber,
        },
    };
};

const POS_PAYMENT_SET_CASH_PAID_CENTS: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {amountCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                cashPaidCents: amountCents,
            },
        },
    };
};

const POS_PAYMENT_ADD_TO_CASH_PAID_CENTS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {amountCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                cashPaidCents:
                    state.currentOrder.payment.cashPaidCents + amountCents,
            },
        },
    };
};

const POS_PAYMENT_SET_CHANGE_OWED_CENTS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {amountCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                changeOwedCents: amountCents,
            },
        },
    };
};

const POS_PAYMENT_APPLY_GIFT_CARD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {giftCard} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                giftCardsApplied: [
                    ...state.currentOrder.payment.giftCardsApplied,
                    giftCard,
                ],
            },
        },
    };
};

const POS_PAYMENT_REMOVE_GIFT_CARD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                giftCardsApplied:
                    state.currentOrder.payment.giftCardsApplied.filter(
                        (gc) => gc.id !== id,
                    ),
            },
        },
    };
};

const POS_PAYMENT_SET_GIFT_CARDS: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {giftCardsApplied: newGiftCardsApplied} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                giftCardsApplied: newGiftCardsApplied,
            },
        },
    };
};

const POS_PAYMENT_APPLY_COMP_CARD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {compCard} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                compCardsApplied: [
                    ...state.currentOrder.payment.compCardsApplied,
                    compCard,
                ],
            },
        },
    };
};

const POS_PAYMENT_REMOVE_COMP_CARD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                compCardsApplied:
                    state.currentOrder.payment.compCardsApplied.filter(
                        (compCard) => compCard.id !== id,
                    ),
            },
        },
    };
};

const POS_PAYMENT_ADD_CREDIT_CARD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {creditCard} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                creditCardsApplied: [
                    ...state.currentOrder.payment.creditCardsApplied,
                    creditCard,
                ],
            },
        },
    };
};

const POS_PAYMENT_APPLY_EZ_CATER: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...defaultState.currentOrder.payment, // no other payment should be applied
                ezCaterApplied: true,
            },
        },
    };
};

const POS_PAYMENT_APPLY_EXTERNAL_TENDER: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {tender: newTender} = action;

    const currentExternalTendersApplied =
        state.currentOrder.payment.externalTendersApplied;
    const newExternalTendersApplied: posModels.AppliedExternalTender[] = [];

    let dupeFound = false;
    for (const tender of currentExternalTendersApplied) {
        if (tender.tenderName === newTender.tenderName) {
            dupeFound = true;
            // merge tenders as they have same name
            newExternalTendersApplied.push({
                tenderName: newTender.tenderName,
                isTaxExempt: newTender.isTaxExempt,
                appliedCents:
                    (tender.appliedCents ?? 0) + newTender.appliedCents,
            });
        } else {
            newExternalTendersApplied.push(tender);
        }
    }

    // add new tender if it didn't need to be merged with another tender
    if (!dupeFound) {
        newExternalTendersApplied.push(newTender);
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                externalTendersApplied: newExternalTendersApplied,
            },
        },
    };
};

const POS_PAYMENT_REMOVE_EXTERNAL_TENDER: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {tenderName} = action;
    const newExternalTendersApplied =
        state.currentOrder.payment.externalTendersApplied.filter(
            (tender) => tender.tenderName !== tenderName,
        );

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                ...state.currentOrder.payment,
                externalTendersApplied: newExternalTendersApplied,
            },
        },
    };
};

const POS_PAYMENT_CLEAR: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            payment: {
                cashPaidCents: 0,
                changeOwedCents: 0,
                giftCardsApplied: [],
                compCardsApplied: [],
                creditCardsApplied: [],
                ezCaterApplied: false,
                externalTendersApplied: [],
            },
        },
    };
};

const POS_ORDERS_SET: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {orders} = action;
    const byId: {[id: string]: posModels.OrderParsed} = {};
    orders.forEach((o: posModels.OrderParsed) => (byId[o.id] = o));

    return {
        ...state,
        orders: {
            ...state.orders,
            byId,
        },
    };
};

const POS_PENDING_ORDERS_SET: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {orders} = action;
    const byId: {[id: string]: posModels.OrderParsed} = {};
    orders.forEach((o: posModels.OrderParsed) => (byId[o.id] = o));

    return {
        ...state,
        pendingOrders: {
            ...state.pendingOrders,
            byId,
        },
    };
};

const POS_PENDING_ORDERS_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {orderId} = action;
    const byId = {...state.pendingOrders.byId};
    delete byId[orderId];

    return {
        ...state,
        pendingOrders: {
            ...state.pendingOrders,
            byId,
        },
    };
};

const POS_ADD_ORDER: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {order} = action;
    const byId: {[id: string]: posModels.OrderParsed} = {...state.orders.byId};
    byId[order.id] = order;

    return {
        ...state,
        orders: {
            ...state.orders,
            byId,
        },
    };
};

const POS_ORDERS_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    const byId = {...state.orders.byId};
    delete byId[id];

    return {
        ...state,
        orders: {
            ...state.orders,
            byId,
        },
    };
};

const POS_PENDING_VOIDED_ITEM_ADD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {pendingVoidedItem} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                pending: {
                    ...state.currentOrderVoided.items.pending,
                    byId: {
                        ...state.currentOrderVoided.items.pending.byId,
                        [pendingVoidedItem.id]: pendingVoidedItem,
                    },
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_COMBO_ADD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {pendingVoidedCombo} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                pending: {
                    ...state.currentOrderVoided.combos.pending,
                    byId: {
                        ...state.currentOrderVoided.combos.pending.byId,
                        [pendingVoidedCombo.id]: pendingVoidedCombo,
                    },
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_ITEM_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    const byId = {...state.currentOrderVoided.items.pending.byId};
    delete byId[id];

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                pending: {
                    ...state.currentOrderVoided.items.pending,
                    byId,
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_COMBO_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    const byId = {...state.currentOrderVoided.combos.pending.byId};
    delete byId[id];

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                pending: {
                    ...state.currentOrderVoided.combos.pending,
                    byId,
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_ITEM_CHANGE_QUANTITY: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, quantity} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                pending: {
                    ...state.currentOrderVoided.items.pending,
                    byId: {
                        ...state.currentOrderVoided.items.pending.byId,
                        [id]: {
                            ...state.currentOrderVoided.items.pending.byId[id],
                            quantity,
                        },
                    },
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_COMBO_CHANGE_QUANTITY: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, quantity} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                pending: {
                    ...state.currentOrderVoided.combos.pending,
                    byId: {
                        ...state.currentOrderVoided.combos.pending.byId,
                        [id]: {
                            ...state.currentOrderVoided.combos.pending.byId[id],
                            quantity,
                        },
                    },
                },
            },
        },
    };
};

const POS_PENDING_VOIDED_CLEAR: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                pending: {
                    byId: {},
                },
            },
            combos: {
                ...state.currentOrderVoided.combos,
                pending: {
                    byId: {},
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_COMBOS_ADD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {voidedCombos} = action;
    const byId = {
        ...state.currentOrderVoided.combos.voided.byId,
    };
    voidedCombos.forEach(
        (combo: posModels.VoidedCombo) => (byId[combo.id] = combo),
    );
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            voidedComboIds: [
                ...state.currentOrder.voidedComboIds,
                ...voidedCombos.map((c: posModels.VoidedCombo) => c.id),
            ],
        },
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                voided: {
                    ...state.currentOrderVoided.combos.voided,
                    byId,
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_ITEMS_ADD: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {voidedItems} = action;
    const byId = {
        ...state.currentOrderVoided.items.voided.byId,
    };
    voidedItems.forEach((item: posModels.VoidedItem) => (byId[item.id] = item));
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            voidedItemIds: [
                ...state.currentOrder.voidedItemIds,
                ...voidedItems.map((i: posModels.VoidedItem) => i.id),
            ],
        },
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                voided: {
                    ...state.currentOrderVoided.items.voided,
                    byId,
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_COMBOS_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    const byId = {
        ...state.currentOrderVoided.combos.voided.byId,
    };
    delete byId[id];

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            voidedComboIds: state.currentOrder.voidedComboIds.filter(
                (cid) => cid !== id,
            ),
        },
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                voided: {
                    ...state.currentOrderVoided.combos.voided,
                    byId,
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_ITEMS_REMOVE: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    const byId = {
        ...state.currentOrderVoided.items.voided.byId,
    };
    delete byId[id];

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            voidedItemIds: state.currentOrder.voidedItemIds.filter(
                (iid) => iid !== id,
            ),
        },
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                voided: {
                    ...state.currentOrderVoided.items.voided,
                    byId,
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_ITEMS_CHANGE_QUANTITY: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, quantity} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            items: {
                ...state.currentOrderVoided.items,
                voided: {
                    ...state.currentOrderVoided.items.voided,
                    byId: {
                        ...state.currentOrderVoided.items.voided.byId,
                        [id]: {
                            ...state.currentOrderVoided.items.voided.byId[id],
                            quantity,
                        },
                    },
                },
            },
        },
    };
};

const POS_CURRENT_VOIDED_COMBOS_CHANGE_QUANTITY: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, quantity} = action;

    return {
        ...state,
        currentOrderVoided: {
            ...state.currentOrderVoided,
            combos: {
                ...state.currentOrderVoided.combos,
                voided: {
                    ...state.currentOrderVoided.combos.voided,
                    byId: {
                        ...state.currentOrderVoided.combos.voided.byId,
                        [id]: {
                            ...state.currentOrderVoided.combos.voided.byId[id],
                            quantity,
                        },
                    },
                },
            },
        },
    };
};

const POS_CURRENT_ORDER_VOIDED_CLEAR: Reducer<PosState> = (state) => {
    if (!state) {
        return defaultState;
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            voidedComboIds: defaultState.currentOrder.voidedComboIds,
            voidedItemIds: defaultState.currentOrder.voidedItemIds,
        },
        currentOrderVoided: defaultState.currentOrderVoided,
    };
};

const POS_ERRORS_SET_DISCOUNT_ERROR_MESSAGE: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {discountErrorMessage} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            discountErrorMessage,
        },
    };
};

const POS_CURRENT_ORDER_ADD_DISCOUNT: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {
        name,
        discountCode,
        rewardId,
        amountCents,
        amountPercentage,
        employeeId,
        managerId,
        reason,
        discountType,
        details,
    } = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            discounts: [
                ...state.currentOrder.discounts,
                {
                    name,
                    discountCode,
                    rewardId,
                    amountCents,
                    amountPercentage,
                    employeeId,
                    managerId,
                    reason,
                    discountType,
                    details,
                },
            ],
        },
    };
};

const POS_CURRENT_ORDER_REMOVE_DISCOUNT: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {discountCode, rewardId} = action;

    let newDiscounts: posModels.DiscountDetails[] = [];
    if (discountCode) {
        newDiscounts = state.currentOrder.discounts.filter(
            (discount) => discount.discountCode !== discountCode,
        );
    } else if (rewardId) {
        newDiscounts = state.currentOrder.discounts.filter(
            (discount) => discount.rewardId !== rewardId,
        );
    }

    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            discounts: newDiscounts,
        },
    };
};

const POS_CURRENT_ORDER_ADD_EXISTING_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, cardNumber, originalBalanceCents, amountToAddCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                addFunds: [
                    ...state.currentOrder.giftCards.addFunds,
                    {id, cardNumber, originalBalanceCents, amountToAddCents},
                ],
            },
        },
    };
};

const POS_CURRENT_ORDER_UPDATE_EXISTING_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, amountToAddCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                addFunds: state.currentOrder.giftCards.addFunds.map((r) =>
                    r.id === id ? {...r, amountToAddCents} : r,
                ),
            },
        },
    };
};

const POS_CURRENT_ORDER_REMOVE_EXISTING_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                addFunds: state.currentOrder.giftCards.addFunds.filter(
                    (gc) => gc.id !== id,
                ),
            },
        },
    };
};

const POS_CURRENT_ORDER_ADD_NEW_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, balanceCents, quantity} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                purchase: [
                    ...state.currentOrder.giftCards.purchase,
                    {id, balanceCents, quantity},
                ],
            },
        },
    };
};

const POS_CURRENT_ORDER_UPDATE_NEW_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id, balanceCents, quantity} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                purchase: state.currentOrder.giftCards.purchase.map((gc) =>
                    gc.id === id ? {...gc, balanceCents, quantity} : gc,
                ),
            },
        },
    };
};

const POS_CURRENT_ORDER_REMOVE_NEW_GIFT_CARD_REQUEST: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {id} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            giftCards: {
                ...state.currentOrder.giftCards,
                purchase: state.currentOrder.giftCards.purchase.filter(
                    (gc) => gc.id !== id,
                ),
            },
        },
    };
};

const POS_CURRENT_ORDER_SET_QSR_SELECTIONS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {selections} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            qsrSelections: selections,
        },
    };
};

const POS_CURRENT_ORDER_SET_CHARITY_AMOUNT_CENTS: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {charityAmountCents} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            charityAmountCents: charityAmountCents,
        },
    };
};

const POS_CURRENT_ORDER_SET_IS_TAX_EXEMPT: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {isTaxExempt} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            isTaxExempt,
        },
    };
};

const POS_CURRENT_ORDER_SET_ALLERGY_WARNING_SHOWN: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {allergyWarningShown} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            allergyWarningShown,
        },
    };
};

const POS_CURRENT_ORDER_SET_RESET_ON_CART_EMPTIED: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {resetOnCartEmptied} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            resetOnCartEmptied,
        },
    };
};

const POS_CURRENT_ORDER_SET_EZ_CATER_ORDER_TYPE: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {ezCaterOrderType} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            ezCaterOrderType,
        },
    };
};

const POS_CURRENT_ORDER_SET_HAVE_PAYMENTS_COVERED_BALANCE: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {havePaymentsCoveredBalance} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            havePaymentsCoveredBalance,
        },
    };
};

const POS_CURRENT_ORDER_SET_IS_SUBMITTED: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {isSubmitted} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            isSubmitted,
        },
    };
};

const POS_CURRENT_ORDER_SET_PRINT_RECEIPT: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {printReceipt} = action;
    return {
        ...state,
        currentOrder: {
            ...state.currentOrder,
            printReceipt,
        },
    };
};

const POS_SET_RESERVE_ORDER_ID_ERRORED: Reducer<PosState> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {reserveOrderIdErrored} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            reserveOrderIdErrored,
        },
    };
};

const POS_ERRORS_SET_PLACE_ORDER_AFTER_PAYMENTS_PROCESSED_ERRORED: Reducer<
    PosState
> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {placeOrderAfterPaymentsProcessedErrored} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            placeOrderAfterPaymentsProcessedErrored,
        },
    };
};

const POS_ERRORS_SET_TILL_ASSIGNMENT_ERROR_MESSAGE: Reducer<PosState> = (
    state,
    action,
) => {
    if (!state) {
        return defaultState;
    }

    const {tillAssignmentErrorMessage} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            tillAssignmentErrorMessage,
        },
    };
};

const POS_ERRORS_SET_CURRENT_ORDER_PLACEMENT_ERROR_MESSAGE: Reducer<
    PosState
> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {currentOrderPlacmentErrorMessage} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            currentOrderPlacmentErrorMessage,
        },
    };
};

const POS_ERRORS_SET_LAST_CART_ENTITY_REMOVAL_ERROR_MESSAGE: Reducer<
    PosState
> = (state, action) => {
    if (!state) {
        return defaultState;
    }

    const {lastCartEntityRemovalErrorMessage} = action;
    return {
        ...state,
        errors: {
            ...state.errors,
            lastCartEntityRemovalErrorMessage,
        },
    };
};

export default createReducer(defaultState, {
    [posActionTypes.POS_SET_DEVICE_ID]: POS_SET_DEVICE_ID,
    [posActionTypes.POS_SET_APP_IS_PARALLEL]: POS_SET_APP_IS_PARALLEL,
    [posActionTypes.POS_STATION_INITIALIZE]: POS_STATION_INITIALIZE,
    [posActionTypes.POS_STATION_EDIT_MODE]: POS_STATION_EDIT_MODE,
    [posActionTypes.POS_STATION_EDIT_AUTO_PRINT_RECEIPTS]:
        POS_STATION_EDIT_AUTO_PRINT_RECEIPTS,
    [posActionTypes.POS_STATION_SET_IS_CC_PAYMENT_DISABLED]:
        POS_STATION_SET_IS_CC_PAYMENT_DISABLED,
    [posActionTypes.POS_STATION_SET_IS_SUBMITTING_ORDER]:
        POS_STATION_SET_IS_SUBMITTING_ORDER,
    [posActionTypes.POS_STATION_TOGGLE_TRAINING_MODE]:
        POS_STATION_TOGGLE_TRAINING_MODE,
    [posActionTypes.POS_STATION_SET_CASHIER]: POS_STATION_SET_CASHIER,
    [posActionTypes.POS_STATION_SET_ASSIGNING_TILL]:
        POS_STATION_SET_ASSIGNING_TILL,
    [posActionTypes.POS_STATION_SET_ASSIGNED_TILL]:
        POS_STATION_SET_ASSIGNED_TILL,
    [posActionTypes.POS_STATION_SET_TRAINING_MODE_ASSIGNED_TILL]:
        POS_STATION_SET_TRAINING_MODE_ASSIGNED_TILL,
    [posActionTypes.POS_STATION_SET_TILL_ASSIGNMENT_SKIPPED]:
        POS_STATION_SET_TILL_ASSIGNMENT_SKIPPED,
    [posActionTypes.POS_ERRORS_SET_TILL_ASSIGNMENT_ERROR_MESSAGE]:
        POS_ERRORS_SET_TILL_ASSIGNMENT_ERROR_MESSAGE,
    [posActionTypes.POS_SET_STORE_SETTINGS]: POS_SET_STORE_SETTINGS,
    [posActionTypes.POS_UPDATE_STORE_SETTINGS]: POS_UPDATE_STORE_SETTINGS,
    [posActionTypes.POS_ALL_PREPS_INITIALIZE]: POS_ALL_PREPS_INITIALIZE,
    [posActionTypes.POS_ADD_ONS_INITIALIZE]: POS_ADD_ONS_INITIALIZE,
    [posActionTypes.POS_CURRENT_ORDER_SET_ID]: POS_CURRENT_ORDER_SET_ID,
    [posActionTypes.POS_CURRENT_ORDER_SET_REF_NUM]:
        POS_CURRENT_ORDER_SET_REF_NUM,
    [posActionTypes.POS_CURRENT_ORDER_SET_ID_REF_NUM_AND_NUMBER]:
        POS_CURRENT_ORDER_SET_ID_REF_NUM_AND_NUMBER,
    [posActionTypes.POS_CURRENT_ORDER_REMOVE_REF_NUM]:
        POS_CURRENT_ORDER_REMOVE_REF_NUM,
    [posActionTypes.POS_CURRENT_ORDER_CLEAR]: POS_CURRENT_ORDER_CLEAR,
    [posActionTypes.POS_CURRENT_ORDER_SET_DRIVE_THRU_DETAILS]:
        POS_CURRENT_ORDER_SET_DRIVE_THRU_DETAILS,
    [posActionTypes.POS_CURRENT_ORDER_SET_CUSTOMER_INFO]:
        POS_CURRENT_ORDER_SET_CUSTOMER_INFO,
    [posActionTypes.POS_CURRENT_ORDER_SET_START_TIME]:
        POS_CURRENT_ORDER_SET_START_TIME,
    [posActionTypes.POS_CURRENT_ORDER_SET_END_TIME]:
        POS_CURRENT_ORDER_SET_END_TIME,
    [posActionTypes.POS_CURRENT_ORDER_SET_ORDER_NUMBER]:
        POS_CURRENT_ORDER_SET_ORDER_NUMBER,
    [posActionTypes.POS_ORDERS_SET]: POS_ORDERS_SET,
    [posActionTypes.POS_PENDING_ORDERS_SET]: POS_PENDING_ORDERS_SET,
    [posActionTypes.POS_PENDING_ORDERS_REMOVE]: POS_PENDING_ORDERS_REMOVE,
    [posActionTypes.POS_ADD_ORDER]: POS_ADD_ORDER,
    [posActionTypes.POS_ORDERS_REMOVE]: POS_ORDERS_REMOVE,
    [posActionTypes.POS_PENDING_VOIDED_ITEM_ADD]: POS_PENDING_VOIDED_ITEM_ADD,
    [posActionTypes.POS_PENDING_VOIDED_COMBO_ADD]: POS_PENDING_VOIDED_COMBO_ADD,
    [posActionTypes.POS_PENDING_VOIDED_ITEM_REMOVE]:
        POS_PENDING_VOIDED_ITEM_REMOVE,
    [posActionTypes.POS_PENDING_VOIDED_COMBO_REMOVE]:
        POS_PENDING_VOIDED_COMBO_REMOVE,
    [posActionTypes.POS_PENDING_VOIDED_ITEM_CHANGE_QUANTITY]:
        POS_PENDING_VOIDED_ITEM_CHANGE_QUANTITY,
    [posActionTypes.POS_PENDING_VOIDED_COMBO_CHANGE_QUANTITY]:
        POS_PENDING_VOIDED_COMBO_CHANGE_QUANTITY,
    [posActionTypes.POS_PENDING_VOIDED_CLEAR]: POS_PENDING_VOIDED_CLEAR,
    [posActionTypes.POS_CURRENT_VOIDED_COMBOS_ADD]:
        POS_CURRENT_VOIDED_COMBOS_ADD,
    [posActionTypes.POS_CURRENT_VOIDED_ITEMS_ADD]: POS_CURRENT_VOIDED_ITEMS_ADD,
    [posActionTypes.POS_CURRENT_VOIDED_COMBOS_REMOVE]:
        POS_CURRENT_VOIDED_COMBOS_REMOVE,
    [posActionTypes.POS_CURRENT_VOIDED_ITEMS_REMOVE]:
        POS_CURRENT_VOIDED_ITEMS_REMOVE,
    [posActionTypes.POS_CURRENT_VOIDED_ITEMS_CHANGE_QUANTITY]:
        POS_CURRENT_VOIDED_ITEMS_CHANGE_QUANTITY,
    [posActionTypes.POS_CURRENT_VOIDED_COMBOS_CHANGE_QUANTITY]:
        POS_CURRENT_VOIDED_COMBOS_CHANGE_QUANTITY,
    [posActionTypes.POS_CURRENT_ORDER_VOIDED_CLEAR]:
        POS_CURRENT_ORDER_VOIDED_CLEAR,
    [posActionTypes.POS_ERRORS_SET_DISCOUNT_ERROR_MESSAGE]:
        POS_ERRORS_SET_DISCOUNT_ERROR_MESSAGE,
    [posActionTypes.POS_CURRENT_ORDER_ADD_DISCOUNT]:
        POS_CURRENT_ORDER_ADD_DISCOUNT,
    [posActionTypes.POS_CURRENT_ORDER_REMOVE_DISCOUNT]:
        POS_CURRENT_ORDER_REMOVE_DISCOUNT,
    [posActionTypes.POS_CURRENT_ORDER_ADD_EXISTING_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_ADD_EXISTING_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_UPDATE_EXISTING_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_UPDATE_EXISTING_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_REMOVE_EXISTING_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_REMOVE_EXISTING_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_ADD_NEW_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_ADD_NEW_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_UPDATE_NEW_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_UPDATE_NEW_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_REMOVE_NEW_GIFT_CARD_REQUEST]:
        POS_CURRENT_ORDER_REMOVE_NEW_GIFT_CARD_REQUEST,
    [posActionTypes.POS_CURRENT_ORDER_SET_QSR_SELECTIONS]:
        POS_CURRENT_ORDER_SET_QSR_SELECTIONS,
    [posActionTypes.POS_CURRENT_ORDER_SET_CHARITY_AMOUNT_CENTS]:
        POS_CURRENT_ORDER_SET_CHARITY_AMOUNT_CENTS,
    [posActionTypes.POS_CURRENT_ORDER_SET_IS_TAX_EXEMPT]:
        POS_CURRENT_ORDER_SET_IS_TAX_EXEMPT,
    [posActionTypes.POS_CURRENT_ORDER_SET_ALLERGY_WARNING_SHOWN]:
        POS_CURRENT_ORDER_SET_ALLERGY_WARNING_SHOWN,
    [posActionTypes.POS_CURRENT_ORDER_SET_RESET_ON_CART_EMPTIED]:
        POS_CURRENT_ORDER_SET_RESET_ON_CART_EMPTIED,
    [posActionTypes.POS_CURRENT_ORDER_SET_EZ_CATER_ORDER_TYPE]:
        POS_CURRENT_ORDER_SET_EZ_CATER_ORDER_TYPE,
    [posActionTypes.POS_CURRENT_ORDER_SET_IS_SUBMITTED]:
        POS_CURRENT_ORDER_SET_IS_SUBMITTED,
    [posActionTypes.POS_CURRENT_ORDER_SET_HAVE_PAYMENTS_COVERED_BALANCE]:
        POS_CURRENT_ORDER_SET_HAVE_PAYMENTS_COVERED_BALANCE,
    [posActionTypes.POS_CURRENT_ORDER_SET_PRINT_RECEIPT]:
        POS_CURRENT_ORDER_SET_PRINT_RECEIPT,
    [posActionTypes.POS_PAYMENT_SET_CASH_PAID_CENTS]:
        POS_PAYMENT_SET_CASH_PAID_CENTS,
    [posActionTypes.POS_PAYMENT_SET_CHANGE_OWED_CENTS]:
        POS_PAYMENT_SET_CHANGE_OWED_CENTS,
    [posActionTypes.POS_PAYMENT_APPLY_GIFT_CARD]: POS_PAYMENT_APPLY_GIFT_CARD,
    [posActionTypes.POS_PAYMENT_REMOVE_GIFT_CARD]: POS_PAYMENT_REMOVE_GIFT_CARD,
    [posActionTypes.POS_PAYMENT_SET_GIFT_CARDS]: POS_PAYMENT_SET_GIFT_CARDS,
    [posActionTypes.POS_PAYMENT_APPLY_COMP_CARD]: POS_PAYMENT_APPLY_COMP_CARD,
    [posActionTypes.POS_PAYMENT_REMOVE_COMP_CARD]: POS_PAYMENT_REMOVE_COMP_CARD,
    [posActionTypes.POS_PAYMENT_ADD_CREDIT_CARD]: POS_PAYMENT_ADD_CREDIT_CARD,
    [posActionTypes.POS_PAYMENT_APPLY_EZ_CATER]: POS_PAYMENT_APPLY_EZ_CATER,
    [posActionTypes.POS_PAYMENT_APPLY_EXTERNAL_TENDER]:
        POS_PAYMENT_APPLY_EXTERNAL_TENDER,
    [posActionTypes.POS_PAYMENT_REMOVE_EXTERNAL_TENDER]:
        POS_PAYMENT_REMOVE_EXTERNAL_TENDER,
    [posActionTypes.POS_PAYMENT_CLEAR]: POS_PAYMENT_CLEAR,
    [posActionTypes.POS_PAYMENT_ADD_TO_CASH_PAID_CENTS]:
        POS_PAYMENT_ADD_TO_CASH_PAID_CENTS,
    [posActionTypes.POS_SET_RESERVE_ORDER_ID_ERRORED]:
        POS_SET_RESERVE_ORDER_ID_ERRORED,
    [posActionTypes.POS_ERRORS_SET_PLACE_ORDER_AFTER_PAYMENTS_PROCESSED_ERRORED]:
        POS_ERRORS_SET_PLACE_ORDER_AFTER_PAYMENTS_PROCESSED_ERRORED,
    [posActionTypes.POS_ERRORS_SET_CURRENT_ORDER_PLACEMENT_ERROR_MESSAGE]:
        POS_ERRORS_SET_CURRENT_ORDER_PLACEMENT_ERROR_MESSAGE,
    [posActionTypes.POS_ERRORS_SET_LAST_CART_ENTITY_REMOVAL_ERROR_MESSAGE]:
        POS_ERRORS_SET_LAST_CART_ENTITY_REMOVAL_ERROR_MESSAGE,
});
